import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  collection,
  getDocs,
  orderBy,
  query,
  where
} from 'firebase/firestore';
import { db } from '../firebase';
import { 
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  CircularProgress,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { TextField } from '@mui/material';
import { format, startOfDay, endOfDay, addDays } from 'date-fns';
import tr from 'date-fns/locale/tr';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SavingsIcon from '@mui/icons-material/Savings';

function Banka() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const sube = queryParams.get('sube') || 'kavakli_sube';
  
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);

  // STATE'LER
  const [toplamBankaBakiye, setToplamBankaBakiye] = useState(0); 
  const [gunlukHavaleGiris, setGunlukHavaleGiris] = useState(0); 
  const [gunlukHavaleCikis, setGunlukHavaleCikis] = useState(0); 
  const [gunlukKrediKarti, setGunlukKrediKarti] = useState(0);   
  const [gunlukGenelGiris, setGunlukGenelGiris] = useState(0);   
  
  const [gunlukIslemler, setGunlukIslemler] = useState([]);      
  const [weeklySummary, setWeeklySummary] = useState([]);        

  const formatTarih = (date) => format(date, 'dd/MM/yyyy', { locale: tr });

  // --- 1. SORGULAR (Veri Çekme) ---
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // A) TOPLAM BANKA BAKİYESİ (Kümülatif)
        const allTimeQuery = query(
          collection(db, 'subeler', sube, 'islemler'),
          where('havaleEft', '>', 0)
        );
        const allTimeSnapshot = await getDocs(allTimeQuery);
        
        let totalBalance = 0;
        allTimeSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const miktar = parseFloat(data.havaleEft) || 0;
          
          if (['Satış', 'Giriş'].includes(data.islemTuru)) {
            totalBalance += miktar;
          } else if (['Alış', 'Çıkış', 'İade', 'Değişim'].includes(data.islemTuru)) {
            totalBalance -= miktar;
          }
        });
        setToplamBankaBakiye(totalBalance);


        // B) GÜNLÜK VERİLER
        const start = startOfDay(selectedDate);
        const end = endOfDay(selectedDate);

        const dailyQuery = query(
          collection(db, 'subeler', sube, 'islemler'),
          where('timestamp', '>=', start),
          where('timestamp', '<=', end),
          orderBy('timestamp', 'desc')
        );
        const dailySnapshot = await getDocs(dailyQuery);
        
        let hGiris = 0;
        let hCikis = 0;
        let kkToplam = 0;
        const gunlukListe = [];

        dailySnapshot.docs.forEach(doc => {
          const data = doc.data();
          const havale = parseFloat(data.havaleEft) || 0;
          const kk = parseFloat(data.krediKarti) || 0;

          if (havale > 0 || kk > 0) {
            gunlukListe.push({ id: doc.id, ...data });
          }

          if (havale > 0) {
            if (['Satış', 'Giriş'].includes(data.islemTuru)) {
              hGiris += havale;
            } else {
              hCikis += havale;
            }
          }

          if (kk > 0) {
            if (['Satış', 'Giriş'].includes(data.islemTuru)) {
                kkToplam += kk;
            }
          }
        });

        setGunlukHavaleGiris(hGiris);
        setGunlukHavaleCikis(hCikis);
        setGunlukKrediKarti(kkToplam);
        setGunlukGenelGiris(hGiris + kkToplam);
        setGunlukIslemler(gunlukListe);


        // C) SON 7 GÜN ÖZETİ
        const sevenDaysAgo = addDays(new Date(), -7);
        const weeklyQuery = query(
          collection(db, 'subeler', sube, 'islemler'),
          where('timestamp', '>=', startOfDay(sevenDaysAgo)),
          where('timestamp', '<=', endOfDay(new Date())),
          orderBy('timestamp', 'asc')
        );
        const weeklySnapshot = await getDocs(weeklyQuery);
        
        const summaryMap = {};
        weeklySnapshot.docs.forEach(doc => {
          const data = doc.data();
          const dateKey = formatTarih(data.timestamp.toDate());
          if (!summaryMap[dateKey]) summaryMap[dateKey] = { havale: 0, kk: 0 };
          const havale = parseFloat(data.havaleEft) || 0;
          const kk = parseFloat(data.krediKarti) || 0;

          if (havale > 0) {
             if (['Satış', 'Giriş'].includes(data.islemTuru)) summaryMap[dateKey].havale += havale;
             else summaryMap[dateKey].havale -= havale;
          }
          if (kk > 0 && ['Satış', 'Giriş'].includes(data.islemTuru)) {
             summaryMap[dateKey].kk += kk;
          }
        });
        setWeeklySummary(Object.entries(summaryMap).reverse());

      } catch (err) {
        console.error("Veri çekme hatası:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [sube, selectedDate]);

  // Kutu Stilleri
  const cardStyle = {
    height: '100%',
    minHeight: '64px',
    minWidth: '290px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: '4px'
  };

  return (
    <Box
      className="bank-page"
      sx={{
        p: 0.7,
        background: 'linear-gradient(180deg, #f8fafd 0%, #eef3f9 100%)',
        height: '96%',
        minHeight: 0,
        borderRadius: '12px',
        border: '1px solid #d9e2ee',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Paper className="bank-header" sx={{ p: 0.8, mb: 1, borderRadius: 2, border: '1px solid #dce5f1', boxShadow: '0 4px 12px rgba(15,23,42,0.06)', background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)', flexShrink: 0 }}>
        <Box className="bank-header-row" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Box className="bank-header-main">
            <Typography sx={{ fontSize: '18px', fontWeight: 900, color: '#1e293b', lineHeight: 1.1 }}>
              Banka ve Finans
            </Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 800, color: '#64748b' }}>
              {sube.toUpperCase().replace("_", " ")}
            </Typography>
          </Box>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={tr}>
            <Box className="bank-date-picker">
            <DatePicker
              label="Gün seçin"
              value={selectedDate}
              onChange={(newValue) => setSelectedDate(newValue)}
              renderInput={(params) => <TextField {...params} size="small" sx={{ bgcolor: 'white', width: 190, '& .MuiInputBase-input': { py: '7px', fontSize: '13px' } }} />}
            />
            </Box>
          </LocalizationProvider>
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box>
      ) : (
        <>
          {/* --- 5 KUTULU BİLGİ ALANI --- */}
          <Grid container spacing={0.8} className="bank-summary-grid" sx={{ mb: 1.1, justifyContent: 'flex-start', flexWrap: 'nowrap', width: '100%', flexShrink: 0 }}>
            
            {/* KUTU 1 */}
            <Grid item className="bank-summary-item">
              <Card className="bank-summary-card" sx={{ ...cardStyle, bgcolor: '#fff', borderLeft: '6px solid #1d4ed8', border: '1px solid #dbe7ff', borderRadius: '10px' }}>
                <CardContent sx={{ p: '6px !important' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.3, color: '#1d4ed8' }}>
                    <AccountBalanceIcon sx={{ fontSize: 20 }} />
                    <Typography sx={{ lineHeight: 1.1, fontSize: '10px', fontWeight: '900' }}>BANKA BAKİYESİ</Typography>
                  </Box>
                  <Typography fontWeight="bold" sx={{ fontSize: '22px', color: '#0f172a', lineHeight: 1 }}>
                    {toplamBankaBakiye.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL
                  </Typography>
                  <Typography sx={{ fontSize: '9px', color: '#64748b', mt: 0 }}>Kümülatif (havale/EFT)</Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* KUTU 2 */}
            <Grid item className="bank-summary-item">
              <Card className="bank-summary-card" sx={{ ...cardStyle, bgcolor: '#fff', borderLeft: '6px solid #16a34a', border: '1px solid #d8f2dd', borderRadius: '10px' }}>
                <CardContent sx={{ p: '6px !important' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.3, color: '#16a34a' }}>
                    <TrendingUpIcon sx={{ fontSize: 20 }} />
                    <Typography sx={{ lineHeight: 1.1, fontSize: '10px', fontWeight: '900' }}>HAVALE GİRİŞ</Typography>
                  </Box>
                  <Typography fontWeight="bold" sx={{ fontSize: '22px', color: '#16a34a', lineHeight: 1 }}>
                    +{gunlukHavaleGiris.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL
                  </Typography>
                  <Typography sx={{ fontSize: '9px', color: '#64748b', mt: 0 }}>Seçilen gün</Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* KUTU 3 */}
            <Grid item className="bank-summary-item">
              <Card className="bank-summary-card" sx={{ ...cardStyle, bgcolor: '#fff', borderLeft: '6px solid #ef4444', border: '1px solid #fde0e0', borderRadius: '10px' }}>
                <CardContent sx={{ p: '6px !important' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.3, color: '#ef4444' }}>
                    <TrendingDownIcon sx={{ fontSize: 20 }} />
                    <Typography sx={{ lineHeight: 1.1, fontSize: '10px', fontWeight: '900' }}>HAVALE ÇIKIŞ</Typography>
                  </Box>
                  <Typography fontWeight="bold" sx={{ fontSize: '22px', color: '#ef4444', lineHeight: 1 }}>
                    -{gunlukHavaleCikis.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL
                  </Typography>
                  <Typography sx={{ fontSize: '9px', color: '#64748b', mt: 0 }}>Seçilen gün</Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* KUTU 4 */}
            <Grid item className="bank-summary-item">
              <Card className="bank-summary-card" sx={{ ...cardStyle, bgcolor: '#fff', borderLeft: '6px solid #f97316', border: '1px solid #ffe1cc', borderRadius: '10px' }}>
                <CardContent sx={{ p: '6px !important' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.3, color: '#f97316' }}>
                    <CreditCardIcon sx={{ fontSize: 20 }} />
                    <Typography sx={{ lineHeight: 1.1, fontSize: '10px', fontWeight: '900' }}>KREDİ KARTI</Typography>
                  </Box>
                  <Typography fontWeight="bold" sx={{ fontSize: '22px', color: '#f97316', lineHeight: 1 }}>
                    {gunlukKrediKarti.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL
                  </Typography>
                  <Typography sx={{ fontSize: '9px', color: '#64748b', mt: 0 }}>Günlük ciro</Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* KUTU 5 */}
            <Grid item className="bank-summary-item">
              <Card className="bank-summary-card" sx={{ ...cardStyle, bgcolor: '#fff', borderLeft: '6px solid #16a34a', border: '1px solid #d8f2dd', borderRadius: '10px' }}>
                <CardContent sx={{ p: '6px !important' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.3, color: '#16a34a' }}>
                    <SavingsIcon sx={{ fontSize: 20 }} />
                    <Typography sx={{ lineHeight: 1.1, fontSize: '10px', fontWeight: '900' }}>GENEL TOPLAM</Typography>
                  </Box>
                  <Typography fontWeight="bold" sx={{ fontSize: '22px', color: '#16a34a', lineHeight: 1 }}>
                    {gunlukGenelGiris.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL
                  </Typography>
                  <Typography sx={{ fontSize: '9px', color: '#64748b', mt: 0 }}>Havale giriş + kart</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* --- ALT BÖLÜM: 2 EŞİT PARÇA (YAN YANA - GENİŞLİK: 750px) --- */}
          <Grid
            container
            spacing={0.6}
            className="bank-tables-wrap"
            sx={{
              width: '100%',
              mt: 1.2,
              flexWrap: 'nowrap',
              flex: 0.9,
              minHeight: 0
            }}
          >
            
            {/* SOL: HAFTALIK ÖZET TABLOSU */}
            <Grid item xs={12} md={4} className="bank-weekly-panel" sx={{ flex: '0 0 36%', maxWidth: '36%' }}>
              <Paper className="bank-weekly-paper" sx={{ 
                borderRadius: 2, 
                overflow: 'hidden', 
                height: '99%',
                display: 'flex', 
                flexDirection: 'column',
                border: '1px solid #dce5f1'
              }}>
                <Box sx={{ bgcolor: '#0f2744', p: 0.7, flexShrink: 0 }}>
                  <Typography sx={{ fontSize: '16px' }} color="white" fontWeight="bold">Son 7 gün özeti</Typography>
                </Box>
                <TableContainer className="bank-weekly-table-wrap" sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
                  <Table className="bank-weekly-table" size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f1f5f9', fontSize: '14px', py: 1 }}>Tarih</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f1f5f9', fontSize: '14px', py: 1 }} align="right">Net havale (TL)</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f1f5f9', fontSize: '14px', py: 1 }} align="right">Kart (TL)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {weeklySummary.map(([date, val]) => (
                        <TableRow key={date} hover>
                          <TableCell sx={{ fontSize: '14px', py: 0.75 }}>{date}</TableCell>
                          <TableCell align="right" sx={{ color: val.havale >= 0 ? 'green' : 'red', fontWeight: 'bold', fontSize: '14px', py: 0.75 }}>
                            {val.havale.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '14px', py: 0.75 }}>{val.kk.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>

            {/* SAĞ: GÜNLÜK İŞLEM LİSTESİ */}
            <Grid item xs={12} md={8} className="bank-daily-panel" sx={{ flex: '0 0 64%', maxWidth: '64%' }}>
              <Paper className="bank-daily-paper" sx={{ 
                borderRadius: 2, 
                overflow: 'hidden', 
                height: '99%',
                display: 'flex', 
                flexDirection: 'column',
                border: '1px solid #dce5f1'
              }}>
                <Box sx={{ bgcolor: '#0f2744', p: 0.7, flexShrink: 0 }}>
                  <Typography sx={{ fontSize: '16px' }} color="white" fontWeight="bold">
                    {formatTarih(selectedDate)} - Banka / Kart işlemleri
                  </Typography>
                </Box>
                <TableContainer className="bank-daily-table-wrap" sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
                  <Table className="bank-daily-table" size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f1f5f9', fontSize: '14px', py: 1 }}>Saat</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f1f5f9', fontSize: '14px', py: 1 }}>No</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f1f5f9', fontSize: '14px', py: 1 }}>Tür</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f1f5f9', fontSize: '14px', py: 1 }}>Kategori / Ürün</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f1f5f9', fontSize: '14px', py: 1 }} align="right">Havale</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f1f5f9', fontSize: '14px', py: 1 }} align="right">Kart</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f1f5f9', fontSize: '14px', py: 1 }}>Açıklama</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {gunlukIslemler.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 2, fontSize: '15px' }}>Bu tarihe ait banka/kart işlemi yok.</TableCell>
                        </TableRow>
                      ) : (
                        gunlukIslemler.map((row) => {
                           let havaleGosterim = row.havaleEft;
                           if(row.havaleEft > 0 && ['Alış','Çıkış','İade','Değişim'].includes(row.islemTuru)) {
                             havaleGosterim = -row.havaleEft;
                           }

                           return (
                            <TableRow key={row.id} hover>
                               <TableCell sx={{ fontSize: '14px', py: 0.75 }}>{row.timestamp?.toDate().toLocaleTimeString('tr-TR', {hour:'2-digit', minute:'2-digit'})}</TableCell>
                               <TableCell sx={{ fontSize: '14px', py: 0.75 }}>{row.islemNo}</TableCell>
                               <TableCell sx={{ py: 0.45 }}>
                                  <Chip 
                                    label={row.islemTuru} 
                                    size="small" 
                                    color={['Satış', 'Giriş'].includes(row.islemTuru) ? 'success' : 'error'} 
                                    variant="outlined"
                                    sx={{ fontSize: '0.84rem', height: '26px' }}
                                  />
                               </TableCell>
                               <TableCell sx={{ fontSize: '14px', py: 0.75 }}>{row.kategori} - {row.urun}</TableCell>
                               <TableCell align="right" sx={{ fontWeight: row.havaleEft > 0 ? 'bold' : 'normal', color: havaleGosterim < 0 ? 'red' : 'inherit', fontSize: '14px', py: 0.75 }}>
                                 {row.havaleEft > 0 ? `${havaleGosterim.toLocaleString('tr-TR')} TL` : '-'}
                               </TableCell>
                               <TableCell align="right" sx={{ fontWeight: row.krediKarti > 0 ? 'bold' : 'normal', fontSize: '14px', py: 0.75 }}>
                                 {row.krediKarti > 0 ? `${row.krediKarti.toLocaleString('tr-TR')} TL` : '-'}
                               </TableCell>
                               <TableCell sx={{ fontSize: '14px', py: 0.75 }}>{row.islemAciklamasi || '-'}</TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>

          </Grid>
        </>
      )}
      <style>{`
        /* --- MOBİL: Banka (masaüstü sx stillerine dokunulmaz) --- */
        @media (max-width: 600px) {
          .bank-page {
            height: auto !important;
            min-height: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            overflow-x: hidden !important;
            overflow-y: visible !important;
            padding: 8px 4px 80px !important;
            box-sizing: border-box !important;
          }

          .bank-header-row {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 10px !important;
          }

          .bank-header-main .MuiTypography-root:first-of-type {
            font-size: 17px !important;
          }

          .bank-date-picker {
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
          }

          .bank-date-picker .MuiFormControl-root,
          .bank-date-picker .MuiTextField-root {
            width: 100% !important;
            max-width: 100% !important;
          }

          .bank-date-picker .MuiInputBase-input {
            font-size: 16px !important;
          }

          .bank-summary-grid {
            flex-wrap: wrap !important;
            gap: 8px !important;
            margin-bottom: 12px !important;
          }

          /* Kartlar tek sutunda alt alta diziliyordu; her birinin sag
             yarisi bostu ve sayfa gereksiz uzuyordu. Ikiserli diziliyor,
             son kart (Genel Toplam) tam satir. */
          .bank-summary-grid > .bank-summary-item {
            width: calc(50% - 4px) !important;
            max-width: calc(50% - 4px) !important;
            flex: 0 0 calc(50% - 4px) !important;
            padding-left: 0 !important;
            padding-top: 0 !important;
          }

          .bank-summary-grid > .bank-summary-item:last-child {
            width: 100% !important;
            max-width: 100% !important;
            flex: 1 1 100% !important;
          }

          /* Etiket (ikonun yanindaki) kucultuluyor, tutar okunakli kaliyor */
          .bank-summary-card .MuiCardContent-root > .MuiBox-root .MuiTypography-root {
            font-size: 9.5px !important;
          }

          .bank-summary-card .MuiCardContent-root > .MuiTypography-root:first-of-type {
            font-size: 18px !important;
          }

          .bank-summary-card {
            min-width: 0 !important;
            width: 100% !important;
            min-height: 66px !important;
          }

          .bank-summary-card .MuiTypography-root {
            word-break: break-word !important;
          }

          .bank-tables-wrap {
            flex-direction: column !important;
            flex-wrap: wrap !important;
            flex: none !important;
            min-height: 0 !important;
            margin-top: 8px !important;
            gap: 12px !important;
          }

          .bank-weekly-panel,
          .bank-daily-panel {
            flex: none !important;
            width: 100% !important;
            max-width: 100% !important;
            padding-left: 0 !important;
            padding-top: 0 !important;
          }

          .bank-weekly-paper,
          .bank-daily-paper {
            height: auto !important;
            min-height: 0 !important;
          }

          .bank-weekly-table-wrap {
            max-height: none !important;
            overflow-y: visible !important;
          }

          .bank-daily-table-wrap {
            max-height: none !important;
            overflow-x: auto !important;
            overflow-y: visible !important;
            -webkit-overflow-scrolling: touch;
          }

          .bank-daily-table {
            min-width: 640px !important;
          }

          .bank-weekly-table .MuiTableCell-root,
          .bank-daily-table .MuiTableCell-root {
            font-size: 13px !important;
            padding: 6px 8px !important;
          }

          .bank-daily-table .MuiChip-root {
            font-size: 11px !important;
            height: 24px !important;
          }
        }
      `}</style>
    </Box>
  );
}

export default Banka;